import { logError } from '../shared/utils/logger';
import { BASE_URL, fetchWithAuth } from './client';

/**
 * Claw9 Gateway API
 * Manage OpenClaw gateway (agents, bindings, channels)
 */

/**
 * Get gateway status
 * GET /gateway/status
 */
export async function getGatewayStatus() {
    const response = await fetchWithAuth(`${BASE_URL}/gateway/status`);

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logError('Get gateway status failed:', response.status, errorData);
        throw new Error(errorData.error?.message || 'Failed to get gateway status');
    }

    return await response.json();
}

/**
 * Get gateway config
 * GET /gateway/config
 */
export async function getGatewayConfig() {
    const response = await fetchWithAuth(`${BASE_URL}/gateway/config`);

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logError('Get gateway config failed:', response.status, errorData);
        throw new Error(errorData.error?.message || 'Failed to get gateway config');
    }

    return await response.json();
}

/**
 * Add agent to gateway
 * POST /gateway/agents
 */
export async function addAgent(params) {
    const response = await fetchWithAuth(`${BASE_URL}/gateway/agents`, {
        method: 'POST',
        body: JSON.stringify({
            agent_id: params.agentId || params.agent_id,
            workspace_path: params.workspacePath || params.workspace_path,
            model: params.model,
            settings: params.settings
        })
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logError('Add agent failed:', response.status, errorData);
        throw new Error(errorData.error?.message || 'Failed to add agent');
    }

    return await response.json();
}

/**
 * Remove agent from gateway
 * DELETE /gateway/agents/{agent_id}
 */
export async function removeAgent(agentId) {
    const response = await fetchWithAuth(`${BASE_URL}/gateway/agents/${encodeURIComponent(agentId)}`, {
        method: 'DELETE'
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logError('Remove agent failed:', response.status, errorData);
        throw new Error(errorData.error?.message || 'Failed to remove agent');
    }

    return await response.json();
}

/**
 * Add channel binding
 * POST /gateway/bindings
 */
export async function addBinding(params) {
    const response = await fetchWithAuth(`${BASE_URL}/gateway/bindings`, {
        method: 'POST',
        body: JSON.stringify({
            agent_id: params.agentId || params.agent_id,
            channel_type: params.channelType || params.channel_type,
            channel_id: params.channelId || params.channel_id
        })
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logError('Add binding failed:', response.status, errorData);
        throw new Error(errorData.error?.message || 'Failed to add binding');
    }

    return await response.json();
}

/**
 * Remove channel binding
 * DELETE /gateway/bindings?agent_id=...&channel_type=...&channel_id=...
 */
export async function removeBinding(agentId, channelType, channelId) {
    const params = new URLSearchParams({
        agent_id: agentId,
        channel_type: channelType,
        channel_id: channelId
    });

    const response = await fetchWithAuth(`${BASE_URL}/gateway/bindings?${params}`, {
        method: 'DELETE'
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logError('Remove binding failed:', response.status, errorData);
        throw new Error(errorData.error?.message || 'Failed to remove binding');
    }

    return await response.json();
}

/**
 * Add channel account (bot token)
 * POST /gateway/channels/{channel_type}/accounts
 */
export async function addChannelAccount(channelType, accountId, token) {
    const response = await fetchWithAuth(`${BASE_URL}/gateway/channels/${channelType}/accounts`, {
        method: 'POST',
        body: JSON.stringify({
            account_id: accountId,
            token: token
        })
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logError('Add channel account failed:', response.status, errorData);
        throw new Error(errorData.error?.message || 'Failed to add channel account');
    }

    return await response.json();
}

/**
 * Test channel token
 * POST /gateway/channels/{channel_type}/test
 */
export async function testChannelToken(channelType, token) {
    const response = await fetchWithAuth(`${BASE_URL}/gateway/channels/${channelType}/test`, {
        method: 'POST',
        body: JSON.stringify({ token })
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logError('Test channel token failed:', response.status, errorData);
        throw new Error(errorData.error?.message || 'Failed to test token');
    }

    return await response.json();
}

/**
 * Restart gateway
 * POST /gateway/restart
 */
export async function restartGateway(options = {}) {
    const response = await fetchWithAuth(`${BASE_URL}/gateway/restart`, {
        method: 'POST',
        body: JSON.stringify({
            graceful: options.graceful ?? true,
            timeout: options.timeout ?? 30
        })
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logError('Restart gateway failed:', response.status, errorData);
        throw new Error(errorData.error?.message || 'Failed to restart gateway');
    }

    return await response.json();
}

/**
 * Reload gateway config
 * POST /gateway/reload
 */
export async function reloadGatewayConfig() {
    const response = await fetchWithAuth(`${BASE_URL}/gateway/reload`, {
        method: 'POST'
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logError('Reload gateway config failed:', response.status, errorData);
        throw new Error(errorData.error?.message || 'Failed to reload config');
    }

    return await response.json();
}

/**
 * Get gateway logs
 * GET /gateway/logs?lines=100&since=1h
 */
export async function getGatewayLogs(options = {}) {
    const params = new URLSearchParams();
    if (options.lines) params.append('lines', options.lines);
    if (options.since) params.append('since', options.since);

    const response = await fetchWithAuth(`${BASE_URL}/gateway/logs?${params}`);

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logError('Get gateway logs failed:', response.status, errorData);
        throw new Error(errorData.error?.message || 'Failed to get logs');
    }

    return await response.json();
}
